import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../dataManagement/uiDb/UiDb";
import { setPermission } from "../dataManagement/uiDb/NotificationsDb";
import {
  syncFcmToken,
  isFcmTokenRegistered,
  reconcileFcmRegistration,
} from "../services/firebase/fcmService";
import { requestAndroidNativeNotificationPermission } from "../services/native/androidBridge";
import {
  requestIosNativeNotificationPermission,
  iosBridgeAvailable,
} from "../services/native/iosBridge";
import {
  isAndroidWebView,
  isIosWebView,
  isPushSupported,
} from "../services/helpers/platform";
import {
  NativeNotificationState,
  PermissionUiState,
  NotificationPermissionMode,
} from "../interfaces/NotificationObject";

// Native permission → UI state. Exhaustive map (not a cast) so a new
// NativeNotificationState variant fails to compile until handled here.
const NATIVE_TO_UI: Record<NativeNotificationState, PermissionUiState> = {
  granted: "granted",
  denied: "denied",
  blocked: "blocked",
  unknown: "granted", // don't nag until told otherwise
};

/**
 * Single, platform-aware source of truth for the notification-permission UI.
 * The Messages banner and ProfileMenu consume this on both platforms.
 *
 * - `web`:    browser Notification API drives state; prompt = requestPermission.
 * - `native`: Android/iOS app; state comes from the bridge (granted/denied/
 *             blocked), prompt = the native requestNotificationPermission which
 *             never dead-ends (shows the dialog or opens Settings). State is
 *             seeded / refreshed by useNativeBridge + onPermissionChanged.
 *
 * Returns `shouldPrompt` (show the banner?), `ctaLabel` ("Enable" vs "Open
 * settings"), `state`, and `promptAndRegister()`.
 */
export function useNotificationPermission() {
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((s: RootState) => s.profile.data);
  const permission = useSelector((s: RootState) => s.notifications.permission);
  const nativePermission = useSelector(
    (s: RootState) => s.notifications.nativePermission
  );

  const nativeAndroid = isAndroidWebView();
  const nativeIos = isIosWebView() || iosBridgeAvailable();
  const webSupported = isPushSupported() && !nativeAndroid && !nativeIos;

  // --- Browser path: keep this device's token registered + self-heal. ---
  useEffect(() => {
    if (!webSupported || !profile) return;
    if (
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    ) {
      return;
    }

    const serverTokens: string[] = Array.isArray(profile.fcmTokens)
      ? profile.fcmTokens
      : [];
    reconcileFcmRegistration(serverTokens);

    const retryIfUnregistered = () => {
      if (!isFcmTokenRegistered()) syncFcmToken();
    };
    window.addEventListener("focus", retryIfUnregistered);
    window.addEventListener("online", retryIfUnregistered);
    return () => {
      window.removeEventListener("focus", retryIfUnregistered);
      window.removeEventListener("online", retryIfUnregistered);
    };
  }, [webSupported, profile]);

  // --- Derived UI state, branched by platform. ---
  let state: PermissionUiState;
  let shouldPrompt: boolean;

  if (nativeAndroid || nativeIos) {
    state = NATIVE_TO_UI[nativePermission];
    shouldPrompt = !!profile && (state === "denied" || state === "blocked");
  } else if (webSupported && typeof Notification !== "undefined") {
    state = Notification.permission;
    // "default" → can prompt; "denied" → browser won't re-prompt, but we still
    // surface guidance to re-enable via site settings instead of going silent.
    shouldPrompt = !!profile && (state === "default" || state === "denied");
  } else {
    state = "unsupported";
    shouldPrompt = false;
  }

  // Whether the CTA can actually do something. Native always can (dialog or
  // Settings). On web only "default" shows a dialog; "denied" is a dead end on
  // web (no programmatic re-prompt / settings), so we show guidance, not a button.
  const canRequest = nativeAndroid || nativeIos || state === "default";

  const ctaLabel = state === "blocked" ? "Open settings" : "Enable";

  // Web resolves synchronously; the native path is async (result arrives via
  // onPermissionChanged), so it returns undefined — callers must ignore it.
  const promptAndRegister =
    useCallback(async (): Promise<NotificationPermission | undefined> => {
      if (nativeAndroid || nativeIos) {
        if (nativeIos) requestIosNativeNotificationPermission();
        else requestAndroidNativeNotificationPermission();
        return undefined;
      }
      if (!webSupported || typeof Notification === "undefined") return "denied";
      const result = await Notification.requestPermission();
      dispatch(setPermission(result));
      if (result === "granted") {
        await syncFcmToken();
      }
      return result;
    }, [nativeAndroid, nativeIos, webSupported, dispatch]);

  const mode: NotificationPermissionMode = nativeAndroid || nativeIos
    ? "native"
    : webSupported
    ? "web"
    : "unsupported";

  return {
    /** True on a real browser with push support (not the WebView). */
    supported: webSupported,
    mode,
    state,
    permission,
    shouldPrompt,
    /** False when the CTA can't do anything (web "denied") — show guidance only. */
    canRequest,
    ctaLabel,
    promptAndRegister,
  };
}
